<template>
  <div>
    <div class="page-header inner">
      <h1 data-test="title">Revenue Dashboard</h1>
      <ManagedDateRange :initialStartDate="startDate" :initialEndDate="endDate" :onApply="setDate"/>
    </div>
    <div class="charts">
      <div data-test="revenue-account-chart">
        <fusioncharts type="bar2d" width="100%" height="600" dataFormat="json" :dataSource="revenueByAccount"/>
      </div>
      <div data-test="revenue-coverage-type-chart">
        <fusioncharts type="stackedbar2d" width="100%" height="600" dataFormat="json" :dataSource="revenueByCoverageType"/>
      </div>
      <div data-test="revenue-carrier-chart">
        <fusioncharts type="bar2d" width="100%" height="600" dataFormat="json" :dataSource="revenueByCarrier"/>
      </div>
      <div data-test="revenue-department-chart">
        <fusioncharts type="bar2d" width="100%" height="600" dataFormat="json" :dataSource="revenueByDepartment"/>
      </div>
    </div>
  </div>
</template>

<script>
import { revenueService } from '@/services/api';
import { chartPrimaryColor, stackedSeriesColors } from '@/utilities/Color';
import ManagedDateRange from '@/components/ManagedDateRange';
import { DateTime } from 'luxon';

const chartDefaults = {
  animation: '1',
  theme: 'cacs',
  showLegend: true,
  legendNumColumns: 1,
  legendPosition: 'right-top',
  showValues: true,
  paletteColors: stackedSeriesColors,
  forceYAxisValueDecimals: false,
  decimals: 1,
  showSum: true
};

export default {
  name: 'RevenueDashboard',
  components: { ManagedDateRange },
  data: () => ({
    revenueData: {
      byAccount: {},
      byCoverageType: {},
      byCarrier: {},
      byDepartment: {}
    },
    startDate: DateTime.local().minus({ year: 1 }).startOf('month').toJSDate(),
    endDate: DateTime.local().minus({ month: 1 }).endOf('month').toJSDate(),
    coverageTypes: [],
    selectedCoverageTypes: [],
    carrierGroups: [],
    selectedCarrierGroups: []
  }),
  computed: {
    revenueByAccount() {
      return { ...this.chartConfig('Total Revenue by Client', {}), data: this.revenueData.byAccount };
    },
    revenueByCoverageType() {
      return { ...this.chartConfig('Total Revenue by Coverage Type', { showValues: false }), ...this.revenueData.byCoverageType };
    },
    revenueByCarrier() {
      return { ...this.chartConfig('Total Revenue by Carrier', {}), data: this.revenueData.byCarrier };
    },
    revenueByDepartment() {
      return { ...this.chartConfig('Total Revenue By Department', {}), data: this.revenueData.byDepartment };
    }
  },
  methods: {
    setDate(datePayload) {
      if (datePayload.startDate) {
        this.startDate = datePayload.startDate;
      }
      if (datePayload.endDate) {
        this.endDate = datePayload.endDate;
      }
      this.refresh();
    },
    async refresh() {
      const [start, end] = [DateTime.fromJSDate(this.startDate).toISODate(), DateTime.fromJSDate(this.endDate).toISODate()];

      const [byAccount, byCarrier, byDepartment, byCoverageType] = (await Promise.all([
        revenueService.getByAccount(...[start, end].map(d => DateTime.fromISO(d))),
        revenueService.getByCarrier(...[start, end].map(d => DateTime.fromISO(d))),
        revenueService.getByDepartment(...[start, end].map(d => DateTime.fromISO(d))),
        revenueService.getByCoverageType(...[start, end].map(d => DateTime.fromISO(d)))
      ])).map(r => {
        if (Array.isArray(r)) {
          return r.map((x) => ({ label: x.label, value: x.value, color: chartPrimaryColor })).slice(0, 20);
        }
        return r;
      });

      const maxDisplayLength = 25;
      byAccount.forEach(c => {
        let cleaned = c.label.trim();
        if (cleaned.length > maxDisplayLength) {
          cleaned = cleaned
              .replace(/,*\s+LLC.*$/, '')
              .replace(/,*\s+Inc\.?$/, '');

          if (cleaned.length > maxDisplayLength) {
            cleaned = cleaned.slice(0, maxDisplayLength) + '…';
          }
        }
        c.label = cleaned;
      });

      this.revenueData = { byAccount, byCoverageType, byCarrier, byDepartment };
    },
    chartConfig(title, extra) {
      return {
        chart: {
          ...chartDefaults,
          ...extra,
          caption: title,
          exportFilename: title
        }
      };
    }
  },
  created() {
    return this.refresh();
  },
};
</script>

<style scoped>
.charts {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(600px, 1fr));
  grid-column-gap: 1em;
  align-items: start;
  grid-gap: 0.5em;
  margin-top: 1rem;
}

.page-header {
  display: grid;
  grid-template-columns: 1fr auto auto;
  align-items: center;
  padding-bottom: 0.5rem;
}
</style>
